import { useEffect, useState } from "react";
import axios from "axios";
import { AnimationPlayer } from "./AnimationPlayer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* Returns true if the animation may still play for this frequency, and a
   function to mark it as shown. */
const freqGate = (freq, presetId) => {
  const key = `va_anim_${presetId}`;
  const today = new Date().toISOString().slice(0, 10);
  try {
    if (freq === "every") return [true, () => {}];
    if (freq === "session") {
      if (sessionStorage.getItem(key)) return [false, () => {}];
      return [true, () => sessionStorage.setItem(key, "1")];
    }
    if (freq === "day") {
      if (localStorage.getItem(key) === today) return [false, () => {}];
      return [true, () => localStorage.setItem(key, today)];
    }
    if (freq === "first") {
      if (localStorage.getItem(key)) return [false, () => {}];
      return [true, () => localStorage.setItem(key, "1")];
    }
  } catch {
    return [true, () => {}];
  }
  return [true, () => {}];
};

const pageAllowed = (pages) => {
  if (pages === "all") return true;
  // "home" => homepage + localized homepages only
  return ["/", "/de", "/fr", "/ar"].includes(window.location.pathname.replace(/\/$/, "") || "/");
};

const deviceAllowed = (devices) => {
  const isMobile = window.innerWidth < 768;
  if (devices === "desktop") return !isMobile;
  if (devices === "mobile") return isMobile;
  return true;
};

export const SeasonalAnimation = () => {
  const [preset, setPreset] = useState(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    // Don't auto-play inside the admin visual editor's preview iframe.
    if (new URLSearchParams(window.location.search).has("anim_edit")) return;
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/seasonal-animation`);
        if (!active || !data?.enabled || !data?.preset) return;
        const p = data.preset;
        if (!pageAllowed(p.pages) || !deviceAllowed(p.devices)) return;
        const [canPlay, mark] = freqGate(data.frequency || "session", p.id);
        if (!canPlay) return;
        setPreset(p);
        setPlaying(true);
        mark();
      } catch {
        /* silent — animation is non-critical */
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (!preset || !playing) return null;

  const device = window.innerWidth < 768 ? "mobile" : "desktop";

  return (
    <AnimationPlayer
      preset={preset}
      device={device}
      soundOn={false}
      onComplete={() => setPlaying(false)}
    />
  );
};
